interface RateLimitConfig {
  windowMs: number; // Time window in milliseconds
  maxRequests: number; // Max requests in window
  burstLimit: number; // Allow burst of X requests immediately
  cooldownMs: number; // Minimum time between requests
}

interface RequestRecord {
  timestamp: number;
  type: string;
}

interface RateLimitResult {
  allowed: boolean;
  remainingCooldown: number; // seconds
  message?: string;
}

export class RateLimiter {
  private requests: RequestRecord[] = [];
  private lastRequestTime: { [key: string]: number } = {};
  
  private config: { [key: string]: RateLimitConfig } = {
    search: {
      windowMs: 60 * 1000, // 1 minute window
      maxRequests: 10, // Max 10 searches per minute
      cooldownMs: 3 * 1000, // 3 seconds between searches
      burstLimit: 3 // Allow 3 immediate searches
    },
    concept: {
      windowMs: 60 * 1000, // 1 minute window
      maxRequests: 10, // Max 10 concept clicks per minute
      cooldownMs: 4 * 1000, // 4 seconds between clicks
      burstLimit: 2 // Allow 2 immediate clicks
    }
  };

  /**
   * Check if a request type is allowed
   */
  canMakeRequest(type: 'search' | 'concept'): RateLimitResult {
    const now = Date.now();
    const config = this.config[type];
    const lastRequest = this.lastRequestTime[type] || 0;
    
    // Clean old requests outside the window
    this.cleanOldRequests(now, config.windowMs);
    
    // Check cooldown (minimum time between requests)
    const timeSinceLastRequest = now - lastRequest;
    if (timeSinceLastRequest < config.cooldownMs) {
      const remainingMs = config.cooldownMs - timeSinceLastRequest;
      return {
        allowed: false,
        remainingCooldown: Math.ceil(remainingMs / 1000),
        message: `Please wait ${Math.ceil(remainingMs / 1000)} seconds before ${type === 'search' ? 'searching' : 'clicking'} again`
      };
    }
    
    // Count requests of this type in the current window
    const requestsInWindow = this.requests.filter(
      r => r.type === type && (now - r.timestamp) < config.windowMs
    ).length;
    
    // Check if we've exceeded the window limit
    if (requestsInWindow >= config.maxRequests) {
      // Find the oldest request to determine when the window resets
      const oldestRequest = this.requests
        .filter(r => r.type === type)
        .reduce((oldest, current) => 
          current.timestamp < oldest.timestamp ? current : oldest
        );
      
      const windowResetTime = oldestRequest.timestamp + config.windowMs;
      const remainingMs = windowResetTime - now;
      
      return {
        allowed: false,
        remainingCooldown: Math.ceil(remainingMs / 1000),
        message: `Rate limit exceeded. Try again in ${Math.ceil(remainingMs / 1000)} seconds`
      };
    }
    
    // Check burst limit (allow immediate requests up to burst limit)
    const recentRequests = this.requests.filter(
      r => r.type === type && (now - r.timestamp) < 10000 // Last 10 seconds
    ).length;
    
    if (recentRequests >= config.burstLimit) {
      // If we've used up our burst, enforce cooldown
      const remainingMs = config.cooldownMs - timeSinceLastRequest;
      if (remainingMs > 0) {
        return {
          allowed: false,
          remainingCooldown: Math.ceil(remainingMs / 1000),
          message: `Please wait ${Math.ceil(remainingMs / 1000)} seconds before ${type === 'search' ? 'searching' : 'clicking'} again`
        };
      }
    }
    
    return {
      allowed: true,
      remainingCooldown: 0
    };
  }
  
  /**
   * Record that a request was made
   */
  recordRequest(type: 'search' | 'concept'): void {
    const now = Date.now();
    this.requests.push({
      timestamp: now,
      type
    });
    this.lastRequestTime[type] = now;
    
    // Keep memory usage reasonable by cleaning old requests
    this.cleanOldRequests(now, Math.max(...Object.values(this.config).map(c => c.windowMs)));
  }
  
  /**
   * Get remaining cooldown time for a request type
   */
  getRemainingCooldown(type: 'search' | 'concept'): number {
    const result = this.canMakeRequest(type);
    return result.remainingCooldown;
  }
  
  /**
   * Clean up old requests outside the time window
   */
  private cleanOldRequests(now: number, maxWindowMs: number): void {
    this.requests = this.requests.filter(
      r => (now - r.timestamp) < maxWindowMs
    );
  }
  
  /**
   * Get current request counts for debugging
   */
  getStats(): { [key: string]: number } {
    const now = Date.now();
    const stats: { [key: string]: number } = {};
    
    for (const type of ['search', 'concept']) {
      const config = this.config[type];
      stats[`${type}_last_minute`] = this.requests.filter(
        r => r.type === type && (now - r.timestamp) < config.windowMs
      ).length;
      stats[`${type}_last_10s`] = this.requests.filter(
        r => r.type === type && (now - r.timestamp) < 10000
      ).length;
    }
    
    return stats;
  }
}

// Export singleton instance
export const rateLimiter = new RateLimiter();